import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getCustomers, getProducts, getSaleById, createSale, updateSale, createCustomer } from '../../services/api';

const SalesForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [newCustomerDialogOpen, setNewCustomerDialogOpen] = useState(false);
  
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [vatRate, setVatRate] = useState(0);
  
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    customer_type: 'retail'
  });
  
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    order_date: new Date().toISOString().split('T')[0],
    delivery_date: '',
    notes: '',
    discount: 0,
    status: 'Draft',
    payment_status: 'Pending',
    payment_method: 'cash',
    warranty_period: 12,
    warranty_terms: '',
    credit_period: 0,
    items: []
  });
  
  const [newItem, setNewItem] = useState({
    product_id: '',
    quantity: 1,
    unit_price: 0,
    discount_percentage: 0,
    warranty_months: 12,
    serial_numbers: []
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [customersResponse, productsResponse] = await Promise.all([
        getCustomers(),
        getProducts()
      ]);
      
      if (customersResponse.success) {
        setCustomers(customersResponse.data);
      }
      
      if (productsResponse.success) {
        const productsData = productsResponse.data;
        setProducts(productsData);
        setFilteredProducts(productsData);
      }
      
      if (isEditMode) {
        const saleResponse = await getSaleById(id);
        if (saleResponse.success) {
          const sale = saleResponse.data;
          const saleCustomer = customersResponse.success 
            ? customersResponse.data.find(c => c.id === sale.customer_id)
            : null;
          
          // Parse serial numbers from items
          const itemsWithParsedSerials = (sale.items || []).map(item => ({
            ...item,
            serial_numbers: Array.isArray(item.serial_numbers) 
              ? item.serial_numbers 
              : (item.serial_numbers ? JSON.parse(item.serial_numbers) : [])
          }));
          
          setFormData({
            customer_id: sale.customer_id,
            customer_name: saleCustomer ? `${saleCustomer.name} - ${saleCustomer.email}` : '',
            order_date: sale.order_date.split('T')[0],
            delivery_date: sale.delivery_date ? sale.delivery_date.split('T')[0] : '',
            notes: sale.notes || '',
            discount: parseFloat(sale.discount) || 0,
            status: sale.status,
            payment_status: sale.payment_status,
            payment_method: sale.payment_method || 'cash',
            warranty_period: sale.warranty_period || 12,
            warranty_terms: sale.warranty_terms || '',
            credit_period: sale.credit_period || 0,
            items: itemsWithParsedSerials
          });
          
          // Set VAT rate from existing sale
          setVatRate(sale.tax_rate || 0);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleVatRateChange = (e) => {
    const newRate = parseFloat(e.target.value) || 0;
    setVatRate(newRate);
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    const tax_amount = subtotal * (vatRate / 100);
    const total = subtotal + tax_amount - (parseFloat(formData.discount) || 0);
    
    return {
      subtotal: subtotal.toFixed(2),
      tax_amount: tax_amount.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const handleCustomerSelect = (event, value) => {
    if (value && value.id) {
      setFormData(prev => ({
        ...prev,
        customer_id: value.id,
        customer_name: `${value.name} - ${value.email}`
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        customer_id: '',
        customer_name: value || ''
      }));
    }
  };

  const handleCustomerInputChange = (event, value) => {
    setFormData(prev => ({
      ...prev,
      customer_name: value
    }));
  };

  const handleAddNewCustomer = async () => {
    try {
      if (!newCustomerData.name.trim()) {
        setError('Customer name is required');
        return;
      }

      const response = await createCustomer(newCustomerData);
      if (response.success) {
        // Refresh customers list
        const customersResponse = await getCustomers();
        if (customersResponse.success) {
          setCustomers(customersResponse.data);
        }
        
        // Auto-select the new customer
        const newCustomer = response.data;
        setFormData(prev => ({ 
          ...prev, 
          customer_id: newCustomer.id,
          customer_name: `${newCustomer.name} - ${newCustomer.email}`
        }));
        
        // Reset new customer form and close dialog
        setNewCustomerData({
          name: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          customer_type: 'retail'
        });
        setNewCustomerDialogOpen(false);
        
        setSuccess('New customer added successfully!');
      } else {
        setError(response.message || 'Failed to add customer');
      }
    } catch (err) {
      setError(err.message || 'Failed to add new customer');
    }
  };

  const handleProductSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredProducts(products);
      return;
    }
    
    const filtered = products.filter(product =>
      product.name.toLowerCase().includes(query.toLowerCase()) ||
      product.code?.toLowerCase().includes(query.toLowerCase()) ||
      product.category?.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredProducts(filtered);
  };

  const handleSelectProductFromSearch = (product) => {
    setNewItem(prev => ({
      ...prev,
      product_id: product.id,
      unit_price: parseFloat(product.price) || 0,
      warranty_months: product.warranty_months || 12
    }));
    setSearchDialogOpen(false);
    setSearchQuery('');
  };

  const calculateItemTotal = (item) => {
    const subtotal = (item.quantity || 0) * (item.unit_price || 0);
    const discount = subtotal * ((item.discount_percentage || 0) / 100);
    return subtotal - discount;
  };

  const handleAddItem = () => {
    if (!newItem.product_id) {
      setError('Please select a product');
      return;
    }
    
    const selectedProduct = products.find(p => p.id === parseInt(newItem.product_id));
    if (!selectedProduct) {
      setError('Selected product not found');
      return;
    }
    
    // Check if product already exists in items
    const existingItemIndex = formData.items.findIndex(item => item.product_id === newItem.product_id);
    
    let updatedItems;
    if (existingItemIndex >= 0) {
      // Update existing item
      updatedItems = [...formData.items];
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        quantity: updatedItems[existingItemIndex].quantity + parseInt(newItem.quantity),
        serial_numbers: [
          ...updatedItems[existingItemIndex].serial_numbers,
          ...newItem.serial_numbers
        ].filter(Boolean)
      };
    } else {
      // Add new item
      const item = {
        product_id: parseInt(newItem.product_id),
        product_name: selectedProduct.name,
        product_code: selectedProduct.code || '',
        quantity: parseInt(newItem.quantity) || 1,
        unit_price: parseFloat(newItem.unit_price) || parseFloat(selectedProduct.price),
        discount_percentage: parseFloat(newItem.discount_percentage) || 0,
        warranty_months: newItem.warranty_months || selectedProduct.warranty_months || formData.warranty_period,
        serial_numbers: newItem.serial_numbers.filter(sn => sn.trim() !== '')
      };
      updatedItems = [...formData.items, item];
    }
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    setNewItem({
      product_id: '',
      quantity: 1,
      unit_price: 0,
      discount_percentage: 0,
      warranty_months: 12,
      serial_numbers: []
    });
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleProductChange = (e) => {
    const productId = e.target.value;
    const selectedProduct = products.find(p => p.id === parseInt(productId));
    if (selectedProduct) {
      setNewItem(prev => ({
        ...prev,
        product_id: productId,
        unit_price: parseFloat(selectedProduct.price) || 0,
        warranty_months: selectedProduct.warranty_months || formData.warranty_period
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      setError('Please select a customer');
      return;
    }
    
    if (formData.items.length === 0) {
      setError('Please add at least one item');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const saleData = {
        ...formData,
        customer_id: parseInt(formData.customer_id),
        discount: parseFloat(formData.discount) || 0,
        warranty_period: parseInt(formData.warranty_period) || 12,
        credit_period: formData.payment_method === 'credit' ? parseInt(formData.credit_period) : 0,
        vat_rate: vatRate, // Send VAT rate to backend
        items: formData.items.map(item => ({
          product_id: parseInt(item.product_id),
          quantity: parseInt(item.quantity),
          unit_price: parseFloat(item.unit_price),
          discount_percentage: parseFloat(item.discount_percentage) || 0,
          warranty_months: parseInt(item.warranty_months) || formData.warranty_period,
          serial_numbers: item.serial_numbers.filter(sn => sn && sn.trim() !== '')
        }))
      };
      
      console.log('Submitting sale data:', saleData);
      
      let response;
      if (isEditMode) {
        response = await updateSale(id, saleData);
      } else {
        response = await createSale(saleData);
      }
      
      if (response.success) {
        setSuccess(isEditMode ? 'Sales order updated successfully!' : 'Sales order created successfully!');
        
        setTimeout(() => {
          navigate('/sales');
        }, 1500);
      } else {
        setError(response.message || 'Failed to save sales order');
      }
    } catch (err) {
      setError(err.message || 'Failed to save sales order');
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateTotals();

  const NewCustomerDialog = () => (
    <Dialog open={newCustomerDialogOpen} onClose={() => setNewCustomerDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonAddIcon />
          Add New Customer
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Customer Name *"
              value={newCustomerData.name}
              onChange={(e) => setNewCustomerData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newCustomerData.email}
              onChange={(e) => setNewCustomerData(prev => ({ ...prev, email: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone Number"
              value={newCustomerData.phone}
              onChange={(e) => setNewCustomerData(prev => ({ ...prev, phone: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              multiline
              rows={2}
              value={newCustomerData.address}
              onChange={(e) => setNewCustomerData(prev => ({ ...prev, address: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="City"
              value={newCustomerData.city}
              onChange={(e) => setNewCustomerData(prev => ({ ...prev, city: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Customer Type</InputLabel>
              <Select
                label="Customer Type"
                value={newCustomerData.customer_type}
                onChange={(e) => setNewCustomerData(prev => ({ ...prev, customer_type: e.target.value }))}
              >
                <MenuItem value="retail">Retail</MenuItem>
                <MenuItem value="wholesale">Wholesale</MenuItem>
                <MenuItem value="corporate">Corporate</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setNewCustomerDialogOpen(false)}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleAddNewCustomer}
          disabled={!newCustomerData.name.trim()}
        >
          Add Customer
        </Button>
      </DialogActions>
    </Dialog>
  );

  const ProductSearchDialog = () => (
    <Dialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SearchIcon />
          Search Products
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Search products by name, code, or category"
          value={searchQuery}
          onChange={(e) => handleProductSearch(e.target.value)}
          margin="normal"
          autoFocus
        />
        
        <TableContainer sx={{ mt: 2, maxHeight: 400 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Price (LKR)</TableCell>
                <TableCell align="right">Stock</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((product) => (
                <TableRow key={product.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {product.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{product.code || '-'}</TableCell>
                  <TableCell>{product.category || '-'}</TableCell>
                  <TableCell align="right">{parseFloat(product.price).toFixed(2)}</TableCell>
                  <TableCell align="right">{product.quantity}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleSelectProductFromSearch(product)}
                    >
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSearchDialogOpen(false)}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/sales')}
        sx={{ mb: 3 }}
      >
        Back to Sales
      </Button>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {isEditMode ? 'Edit Sales Order' : 'Create New Sales Order'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Customer and Dates */}
            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Autocomplete
                  freeSolo
                  options={customers}
                  getOptionLabel={(option) => 
                    typeof option === 'string' 
                      ? option 
                      : `${option.name} - ${option.email}`
                  }
                  value={formData.customer_name}
                  onChange={handleCustomerSelect}
                  onInputChange={handleCustomerInputChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer *"
                      required
                      placeholder="Search or select customer"
                      error={!formData.customer_id}
                      helperText={!formData.customer_id ? "Please select a customer" : ""}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {option.email} | {option.phone || 'No phone'}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setNewCustomerDialogOpen(true)}
                  fullWidth
                >
                  Add New Customer
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Order Date"
                value={formData.order_date}
                onChange={(e) => setFormData(prev => ({ ...prev, order_date: e.target.value }))}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Delivery Date"
                value={formData.delivery_date}
                onChange={(e) => setFormData(prev => ({ ...prev, delivery_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Payment and Credit Section */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Payment Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Payment Method</InputLabel>
                        <Select
                          label="Payment Method"
                          value={formData.payment_method}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            payment_method: e.target.value,
                            payment_status: e.target.value === 'credit' ? 'Pending' : prev.payment_status
                          }))}
                        >
                          <MenuItem value="cash">Cash</MenuItem>
                          <MenuItem value="card">Card</MenuItem>
                          <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                          <MenuItem value="cheque">Cheque</MenuItem>
                          <MenuItem value="credit">Credit</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    {formData.payment_method === 'credit' && (
  <>
    <Grid item xs={12}>
      <TextField
        fullWidth
        type="number"
        label="Credit Period (Days)"
        value={formData.credit_period}
        onChange={(e) => setFormData(prev => ({ ...prev, credit_period: e.target.value }))}
        inputProps={{ min: 0, max: 365 }}
        helperText="Number of days for credit payment"
      />
    </Grid>
    <Grid item xs={12}>
      <Alert severity="info" variant="outlined">
        Credit sale - Payment due in {formData.credit_period || 30} days
      </Alert>
    </Grid>
  </>
)}
                    
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Payment Status"
                        value={formData.payment_status}
                        onChange={(e) => setFormData(prev => ({ ...prev, payment_status: e.target.value }))}
                      >
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Partial">Partial</MenuItem>
                        <MenuItem value="Paid">Paid</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Warranty Section */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Warranty Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Default Warranty Period (Months)"
                        value={formData.warranty_period}
                        onChange={(e) => setFormData(prev => ({ ...prev, warranty_period: e.target.value }))}
                        inputProps={{ min: 0, max: 120 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Warranty Terms"
                        value={formData.warranty_terms}
                        onChange={(e) => setFormData(prev => ({ ...prev, warranty_terms: e.target.value }))}
                        placeholder="e.g., Manufacturer warranty..."
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Add Items Section */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      Add Items
                    </Typography>
                    <Button
                      startIcon={<SearchIcon />}
                      variant="outlined"
                      onClick={() => setSearchDialogOpen(true)}
                    >
                      Search Products
                    </Button>
                  </Box>
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth>
                        <InputLabel>Product</InputLabel>
                        <Select
                          label="Product"
                          value={newItem.product_id}
                          onChange={handleProductChange}
                        >
                          <MenuItem value="">Select Product</MenuItem>
                          {products.map((product) => (
                            <MenuItem key={product.id} value={product.id}>
                              {product.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={6} md={1}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Qty"
                        value={newItem.quantity}
                        onChange={(e) => setNewItem(prev => ({ ...prev, quantity: e.target.value }))}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Unit Price (LKR)"
                        value={newItem.unit_price}
                        onChange={(e) => setNewItem(prev => ({ ...prev, unit_price: e.target.value }))}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    
                    <Grid item xs={6} md={1}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Disc %"
                        value={newItem.discount_percentage}
                        onChange={(e) => setNewItem(prev => ({ ...prev, discount_percentage: e.target.value }))}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                      />
                    </Grid>
                    
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Warranty (Months)"
                        value={newItem.warranty_months}
                        onChange={(e) => setNewItem(prev => ({ ...prev, warranty_months: e.target.value }))}
                        inputProps={{ min: 0, max: 120 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Serial Numbers"
                        placeholder="SN1, SN2, SN3"
                        value={newItem.serial_numbers.join(', ')}
                        onChange={(e) => setNewItem(prev => ({ 
                          ...prev, 
                          serial_numbers: e.target.value.split(',').map(sn => sn.trim()).filter(Boolean)
                        }))}
                        helperText="Separate by commas"
                      />
                    </Grid>
                    
                    <Grid item xs={6} md={1}>
                      <Typography variant="body2" align="right">
                        LKR {calculateItemTotal(newItem).toFixed(2)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6} md={1}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddItem}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Items Table */}
            {formData.items.length > 0 && (
              <Grid item xs={12}>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Disc %</TableCell>
                        <TableCell align="right">Warranty</TableCell>
                        <TableCell>Serial Numbers</TableCell>
                        <TableCell align="right">Line Total</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {item.product_name}
                            </Typography>
                            {item.product_code && (
                              <Typography variant="caption" color="textSecondary">
                                Code: {item.product_code}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{parseFloat(item.unit_price).toFixed(2)}</TableCell>
                          <TableCell align="right">{item.discount_percentage}%</TableCell>
                          <TableCell align="right">{item.warranty_months} months</TableCell>
                          <TableCell>
                            <Box sx={{ maxWidth: 200 }}>
                              {item.serial_numbers && item.serial_numbers.map((serial, serialIndex) => (
                                <Chip
                                  key={serialIndex}
                                  label={serial}
                                  size="small"
                                  sx={{ m: 0.5 }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{calculateItemTotal(item).toFixed(2)}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveItem(index)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {/* Notes and Totals */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
            
            {/* Totals with Editable VAT */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Order Summary
                  </Typography>
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Subtotal:</Typography>
                    <Typography>LKR {totals.subtotal}</Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography>VAT Rate:</Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        size="small"
                        type="number"
                        value={vatRate}
                        onChange={handleVatRateChange}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                        sx={{ width: 100 }}
                      />
                      <Typography>%</Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>VAT Amount:</Typography>
                    <Typography>LKR {totals.tax_amount}</Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Discount:</Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={formData.discount}
                      onChange={(e) => setFormData(prev => ({ ...prev, discount: e.target.value }))}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{ width: 100 }}
                    />
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6">Total:</Typography>
                    <Typography variant="h6" color="primary">
                      LKR {totals.total}
                    </Typography>
                  </Box>
                  
                  {vatRate === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }} variant="outlined">
                      VAT rate is 0%. Change to 15% for standard VAT calculation.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/sales')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving || !formData.customer_id || formData.items.length === 0}
                >
                  {saving ? 'Saving...' : isEditMode ? 'Update Order' : 'Create Order'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Dialogs */}
      <NewCustomerDialog />
      <ProductSearchDialog />
    </Container>
  );
};

export default SalesForm;